import { hash01 } from "./introDrawing";
import { PassportStamp, pickEvenly } from "./passportStamps";

/**
 * What the boarding pass, the tickets and the two boards show, and when. Pure functions of the trip and
 * the frame, so BoardingIntro.tsx and DepartureBoardIntro.tsx only draw.
 */

/** Kilometres along a leg as the map draws it, great-circle between its points; a MultiLineString's parts added up. */
export const legKm = (geometry: any): number => {
	const lines: number[][][] = geometry?.type === "MultiLineString" ? geometry.coordinates || [] : [geometry?.coordinates || []];
	const r = Math.PI / 180;
	let km = 0;
	lines.forEach((line) => {
		for (let i = 1; i < (line || []).length; i++) {
			const [lng1, lat1] = line[i - 1];
			const [lng2, lat2] = line[i];
			const a = Math.sin(((lat2 - lat1) * r) / 2) ** 2 + Math.cos(lat1 * r) * Math.cos(lat2 * r) * Math.sin(((lng2 - lng1) * r) / 2) ** 2;
			km += 12742 * Math.asin(Math.sqrt(a));
		}
	});
	return km;
};

export type TripLeg = { from: string; to: string; when: string; glyph: string; km: number };

/** A leg between each two stops: from, to, the day it arrives, the glyph it travels by, how far. */
export const tripLegs = (stamps: PassportStamp[], paths: any[]): TripLeg[] =>
	stamps.slice(1).map((to, i) => ({
		from: stamps[i].city,
		to: to.city,
		when: to.when,
		glyph: to.arrivedBy ?? "car",
		km: Math.round(legKm(paths?.[i]?.geometry)),
	}));

/**
 * The codes and cities on the pass. A marketing video names no real flight, so the codes come from the
 * request (`intro.from`, `intro.to`); without them the pass leaves from HOME for the first stop.
 */
export const passCodes = (config: { from?: string; to?: string; fromCity?: string }, reelData: any[]) => {
	const firstCity = String(reelData?.[0]?.city || "");
	// Letters only, accents dropped: Ho Chi Minh City is HOC, not "HO ", and Évora is EVO.
	const letters = firstCity.normalize("NFD").replace(/[^A-Za-z]/g, "");
	return {
		from: String(config.from || "HOME")
			.toUpperCase()
			.slice(0, 4),
		to: String(config.to || (letters || firstCity).slice(0, 3))
			.toUpperCase()
			.slice(0, 4),
		fromCity: config.fromCity || "",
		toCity: firstCity,
	};
};

/** Poppins ExtraBold's advance per character, in ems, read from the font: the pass's codes are set in it. */
const CODE_ADVANCE: Record<string, number> = {
	A: 0.755,
	B: 0.671,
	C: 0.758,
	D: 0.735,
	E: 0.548,
	F: 0.561,
	G: 0.757,
	H: 0.743,
	I: 0.308,
	J: 0.584,
	K: 0.726,
	L: 0.491,
	M: 0.935,
	N: 0.765,
	O: 0.787,
	P: 0.636,
	Q: 0.788,
	R: 0.661,
	S: 0.62,
	T: 0.604,
	U: 0.712,
	V: 0.747,
	W: 1.076,
	X: 0.739,
	Y: 0.701,
	Z: 0.613,
	"0": 0.657,
	"1": 0.387,
	"2": 0.569,
	"3": 0.61,
	"4": 0.691,
	"5": 0.655,
	"6": 0.635,
	"7": 0.523,
	"8": 0.653,
	"9": 0.606,
};

/** How wide a code sets at 1em, with the pass's -0.02em tracking. A character not in the table counts as an M. */
export const codeWidth = (code: string) => [...code].reduce((w, ch) => w + (CODE_ADVANCE[ch] ?? CODE_ADVANCE.M) - 0.02, 0);

/**
 * The codes' size and the ends of the plane's arc between them, in units of the 980-wide pass whose
 * main part is `width` wide: the arc's ends stand `clear` off each code (half the plane and a gap), and
 * the codes shrink from `size` until the arc has at least `minArc` -- HOME to GIZ does not fit at 112.
 */
export const passLayout = (from: string, to: string, { width = 748, margin = 40, size = 112, clear = 34, minArc = 130 } = {}) => {
	const words = codeWidth(from) + codeWidth(to);
	const fit = words > 0 ? Math.min(size, (width - 2 * margin - 2 * clear - minArc) / words) : size;
	return { size: fit, arcFrom: margin + codeWidth(from) * fit + clear, arcTo: width - margin - codeWidth(to) * fit - clear };
};

/** The pass's and the board's decoration, the same on every video: it is for no real flight. */
export const PASS_DECOR = { passenger: "YOU", boarding: "09:40", gate: "07", seat: "1A" };

/** The characters on a split-flap, in the order they fall. */
export const FLAPS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";

/** What a spinning flap shows on `frame`: a new character every frame, the same one on every render. */
export const flapChar = (seed: number, frame: number) => FLAPS[Math.floor(hash01(seed * 7919 + frame * 104729) * FLAPS.length)];

// ---- where-to-next -----------------------------------------------------------------------------------

/** The places the wheel spins through before it lands. Short, far apart, recognisable on a flap. */
export const WORLD = [
	"TOKYO",
	"LIMA",
	"OSLO",
	"BALI",
	"ROME",
	"HAVANA",
	"CUSCO",
	"SEOUL",
	"QUITO",
	"FIJI",
	"LISBON",
	"HANOI",
	"DUBAI",
	"SYDNEY",
	"MEXICO",
	"NAIROBI",
];
const WHEEL_MAX = 12;

/** Cut a name to `n` characters at a word, not inside one: HO CHI MINH CITY in ten is HO CHI MINH. */
export const fitName = (name: string, n: number) => {
	if (name.length <= n) return name;
	const cut = name.slice(0, n + 1).lastIndexOf(" ");
	return (cut > 0 ? name.slice(0, cut) : name.slice(0, n)).trim();
};

/** What the wheel lands on: the trip's country, or its first stop when there is none or it is too long. */
export const wheelTarget = (currentTrip: any, reelData: any[]) => {
	const country = String(currentTrip?.countries?.[0]?.name || "").toUpperCase();
	const first = String(reelData?.[0]?.city || "").toUpperCase();
	return country && country.length <= WHEEL_MAX ? country : fitName(first || country, WHEEL_MAX);
};

/**
 * The wheel on `frame`: as wide as the target (at least five), spinning from `spin` through the places
 * that fit, never the target itself, one after `gaps[i]` frames -- slowing -- and then landing.
 */
export const wheelAt = (frame: number, target: string, spin: number, gaps: number[]) => {
	const width = Math.max(5, target.length);
	const words = WORLD.filter((w) => w.length <= width && w !== target && !target.includes(w));
	const centred = (w: string) => (" ".repeat(Math.floor((width - w.length) / 2)) + w).padEnd(width, " ");
	// The frame each place comes up on, and the one the destination lands on, whatever the frame now.
	const starts: number[] = [];
	let landAt = spin;
	for (const gap of gaps) {
		starts.push(landAt);
		landAt += gap;
	}
	if (frame >= landAt) return { width, text: centred(target), changedAt: landAt, landAt, landed: true };
	const i = Math.max(0, starts.filter((s) => frame >= s).length - 1);
	return { width, text: centred(words[i % Math.max(1, words.length)] ?? ""), changedAt: starts[i], landAt, landed: false };
};

// ---- departure-board ---------------------------------------------------------------------------------

export type BoardRow = { when: string; city: string; via: string | null; nights: string; remark: "BOARDING" | "ON TIME" | "DAY STOP" };

/**
 * A row a stop, at most `max`, first and last kept: its date, its name cut to fit, how you get there,
 * its nights. The first stop is BOARDING, a stop passed through is a DAY STOP, the rest ON TIME.
 */
export const boardRows = (stamps: PassportStamp[], max: number, cityWidth: number): BoardRow[] =>
	pickEvenly(stamps, max).map((s, r) => {
		const dayStop = s.nights === "DAY STOP";
		return {
			when: s.when,
			city: fitName(s.city, cityWidth),
			via: s.arrivedBy,
			nights: dayStop ? "0" : s.nights.replace(/\D/g, ""),
			remark: r === 0 ? "BOARDING" : dayStop ? "DAY STOP" : "ON TIME",
		};
	});
